// PR-16.3 — file-upload UX for sponsor logos. Replaces the plain URL
// Input from PR-16.1's <SponsorsSection>. Used twice in LeagueForm
// (one per variant: my_series + register).
//
// Behavior:
//   - File picker (hidden input + Button) accepts PNG / JPEG / WebP
//     up to 2 MiB
//   - Validation runs client-side via validateSponsorImageFile() before
//     burning a round-trip on a too-large or wrong-MIME file
//   - Upload to logos/leagues/<leagueId>/sponsor_<variant>.<ext>
//     with upsert: true (re-uploading same variant replaces the
//     previous file)
//   - getPublicUrl() + cache-buster ?v=Date.now() so the thumbnail
//     refreshes immediately
//   - Sets the URL into the parent form via onChange(url)
//   - "Ta bort" deletes the storage object when the URL clearly came
//     from our bucket (heuristic match), then nulls the field. Pasted
//     external URLs are merely cleared (don't touch the third-party
//     file)
//   - URL fallback: a small Input below the file picker still accepts
//     pasted external URLs so admins who already have a hosted logo
//     can keep using PR-16.1's flow
//
// Disabled when leagueId is undefined (new league not yet saved):
// shows a hint that admin must save the series first. Mirrors how the
// LeagueRoundsManager (PR-10.1) gates its CRUD on hasId.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import {
  validateSponsorImageFile,
  sponsorStoragePath,
  isOurLogosBucketUrl,
  extractLogosBucketKey,
  type SponsorVariant,
} from "@/lib/sponsorUploadHelpers";

const ACCEPT_ATTR = "image/png,image/jpeg,image/webp";

export default function SponsorLogoUpload({
  leagueId,
  variant,
  value,
  onChange,
}: {
  /** undefined when admin is creating a new league — picker is disabled */
  leagueId: string | undefined;
  variant: SponsorVariant;
  value: string;
  onChange: (url: string) => void;
}) {
  const { t } = useTranslation("admin");
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const disabled = !leagueId;

  const handlePick = () => {
    if (!leagueId) return;
    inputRef.current?.click();
  };

  const handleFile = async (file: File | null) => {
    if (!file || !leagueId) return;
    const validation = validateSponsorImageFile(file);
    if (!validation.ok) {
      const messageKey =
        validation.error === "too_large"
          ? t("leagues.form.sponsorErrTooLarge", "Filen är större än 2 MiB. Välj en mindre bild.")
          : validation.error === "wrong_type"
            ? t("leagues.form.sponsorErrWrongType", "Endast PNG, JPEG och WebP stöds.")
            : t("leagues.form.sponsorErrNoFile", "Ingen fil vald.");
      toast.error(messageKey);
      return;
    }

    const path = sponsorStoragePath(leagueId, variant, validation.ext);
    setUploading(true);
    try {
      const { error: uploadErr } = await supabase.storage
        .from("logos")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
      // Cache-buster: same path on re-upload reuses the same URL; the
      // ?v= param forces the <img> to refetch.
      const url = `${urlData.publicUrl}?v=${Date.now()}`;
      onChange(url);
      toast.success(t("leagues.form.sponsorUploadOk", "Sponsorlogga uppladdad"));
    } catch (err) {
      console.error("Sponsor logo upload failed:", err);
      toast.error(
        t(
          "leagues.form.sponsorUploadFailed",
          "Kunde inte ladda upp logga: ",
        ) + (err instanceof Error ? err.message : ""),
      );
    } finally {
      setUploading(false);
      // Clear the input so picking the same file again still triggers onChange
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async () => {
    if (!value) return;
    setRemoving(true);
    try {
      // Delete the storage object only if the URL clearly came from our
      // bucket (don't touch third-party hosted files).
      if (isOurLogosBucketUrl(value)) {
        const key = extractLogosBucketKey(value);
        if (key) {
          const { error: delErr } = await supabase.storage
            .from("logos")
            .remove([key]);
          if (delErr) {
            // Non-fatal — null the form field even if storage delete
            // failed (admin can re-clean later via Supabase dashboard).
            console.warn("Sponsor logo storage delete failed (non-fatal):", delErr);
          }
        }
      }
      onChange("");
      toast.success(t("leagues.form.sponsorRemoved", "Sponsorlogga borttagen"));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePick}
          disabled={disabled || uploading || removing}
          className="gap-1"
        >
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {value
            ? t("leagues.form.sponsorReplace", "Byt logga")
            : t("leagues.form.sponsorUpload", "Ladda upp logga")}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={uploading || removing}
            className="gap-1 text-destructive hover:text-destructive"
          >
            {removing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            {t("leagues.form.sponsorRemove", "Ta bort")}
          </Button>
        )}
      </div>

      {/* URL fallback — admin can still paste an external URL (PR-16.1
          compat). Reads/writes the same form field as the upload path. */}
      <div className="space-y-1">
        <p className="text-[11px] text-muted-foreground">
          {t("leagues.form.sponsorOrPasteUrl", "Eller klistra in URL till logga (max 2 MiB om uppladdad)")}:
        </p>
        <Input
          value={value}
          placeholder="https://…/sponsor.png"
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      {/* Live thumbnail (mirrors PR-16.1 SponsorThumb pattern) */}
      {value && <SponsorThumb url={value} />}

      {disabled && (
        <p className="text-[11px] text-muted-foreground italic">
          {t(
            "leagues.form.sponsorSaveFirst",
            "Spara serien först för att kunna ladda upp en logga.",
          )}
        </p>
      )}
    </div>
  );
}

function SponsorThumb({ url }: { url: string }) {
  const [loaded, setLoaded] = useState(true);
  if (!loaded) return null;
  return (
    <div className="border rounded p-2 bg-muted/30 inline-block">
      <img
        src={url}
        alt="Sponsor"
        className="h-12 w-auto object-contain"
        onError={() => setLoaded(false)}
      />
    </div>
  );
}
